import React, { useEffect, useRef } from 'react';
import { Chart, RadarController, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend } from 'chart.js';
import { DEPENDENCY_WEIGHTS } from '../../utils/algorithms';

// Register Chart.js components
Chart.register(RadarController, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

function RadarChart({ formData }) {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    // Get current scores
    const currentScores = DEPENDENCY_WEIGHTS.map(dep => formData[`score${dep.id}`]);
    
    // Target is all 10s
    const targetScores = DEPENDENCY_WEIGHTS.map(() => 10);

    // Destroy existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Create new chart
    const ctx = chartRef.current.getContext('2d');
    chartInstance.current = new Chart(ctx, {
      type: 'radar',
      data: {
        labels: DEPENDENCY_WEIGHTS.map(dep => `${dep.id}. ${dep.name}`),
        datasets: [
          {
            label: 'Current State',
            data: currentScores,
            backgroundColor: 'rgba(134, 194, 202, 0.2)',
            borderColor: '#86C2CA',
            borderWidth: 2,
            pointBackgroundColor: '#86C2CA',
            pointBorderColor: '#fff',
            pointHoverBackgroundColor: '#fff',
            pointHoverBorderColor: '#86C2CA'
          },
          {
            label: 'Target State',
            data: targetScores,
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            borderColor: '#10b981',
            borderWidth: 2,
            borderDash: [5, 5],
            pointBackgroundColor: '#10b981',
            pointBorderColor: '#fff',
            pointHoverBackgroundColor: '#fff',
            pointHoverBorderColor: '#10b981'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        layout: {
            padding: {
                top: 20,
                right: 80,
                bottom: 20,
                left: 80
            }
            },
        scales: {
          r: {
            beginAtZero: true,
            max: 10,
            ticks: {
              stepSize: 2,
              font: {
                size: 10
              }
            },
            pointLabels: {
                font: {
                    size: 11
                },
                color: '#1e293b',
                callback: function(label) {
                    // Split long labels into multiple lines
                    const maxLength = 30;
                    if (label.length <= maxLength) return label;
                    
                    const words = label.split(' ');
                    const lines = [];
                    let currentLine = words[0];
                    
                    for (let i = 1; i < words.length; i++) {
                    if ((currentLine + ' ' + words[i]).length <= maxLength) {
                        currentLine += ' ' + words[i];
                    } else {
                        lines.push(currentLine);
                        currentLine = words[i];
                    }
                    }
                    lines.push(currentLine);
                    return lines;
                }
                },
          }
        },
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              padding: 20,
              font: {
                size: 12
              }
            }
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                return `${context.dataset.label}: ${context.parsed.r}`;
              }
            }
          }
        }
      }
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [formData]);

  return (
    <div className="w-full max-w-3xl mx-auto p-4">
      <canvas ref={chartRef}></canvas>
    </div>
  );
}

export default RadarChart;